import React from 'react';
import { reportError } from '../../services/crashReporting';

const RELOADED_KEY = 'ultronic_reloaded_for_update';

export const isStaleVersionError = (error) =>
  error?.name === 'ChunkLoadError' || /Loading (CSS )?chunk [\w-]+ failed/i.test(String(error?.message || ''));

function reloadOnce() {
  try {
    if (sessionStorage.getItem(RELOADED_KEY)) return false;
    sessionStorage.setItem(RELOADED_KEY, String(Date.now()));
  } catch {
    return false;
  }
  window.location.reload();
  return true;
}

export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, attempts: 0 };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    // After a new version is published, a tab still open on the old one asks
    // for files that are gone. Load the new version once, without a message.
    if (isStaleVersionError(error) && reloadOnce()) return;
    console.error('ErrorBoundary caught:', error, info);
    reportError(error, { componentStack: info?.componentStack });
  }

  retry = () => this.setState((state) => ({ hasError: false, attempts: state.attempts + 1 }));

  goHome = () => {
    window.location.hash = '#/';
    this.setState({ hasError: false });
  };

  // Last resort when the saved plan itself is what breaks the screen.
  resetPlan = () => {
    try {
      Object.keys(localStorage)
        .filter((key) => key.startsWith('financialPlanState_v2'))
        .forEach((key) => localStorage.removeItem(key));
    } catch {
      // Storage unavailable: reloading still gives a clean start.
    }
    window.location.hash = '#/';
    window.location.reload();
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div className="min-h-[300px] flex items-center justify-center px-4" role="alert">
        <div className="terminal-card p-6 sm:p-8 max-w-md text-center">
          <h2 className="text-lg font-display font-bold text-terminal-red uppercase tracking-wider mb-2">
            Something went wrong
          </h2>
          <p className="text-txt-secondary text-sm mb-5">
            This screen hit a problem. Your saved plan hasn&apos;t changed.
          </p>
          <div className="flex flex-col gap-2">
            <button onClick={this.retry} className="glow-btn glow-btn-green font-mono text-sm">Try again</button>
            <button onClick={this.goHome} className="glow-btn glow-btn-amber font-mono text-sm">Go home</button>
            {this.state.attempts > 0 && (
              <>
                <p className="text-txt-secondary text-xs mt-3">
                  Still not working? You can clear the plan saved on this device and start over. A plan synced to your
                  account won&apos;t be touched.
                </p>
                <button onClick={this.resetPlan} className="glow-btn glow-btn-red font-mono text-sm">
                  Clear plan on this device
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }
}
